import React from "react";
import youtubeLogo from "@/assets/icons/youtubeIcon.svg";
import denmarkLogo from "@/assets/icons/svg/denmark_fav.svg";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { Playlist } from "./playlistData";

/** Pin colours, shared by the picker rows and the Manage sheet (also used in the CMS Manage list). */
export const PIN_ACTIVE = "#0d6efd";
export const PIN_IDLE = "#6ea8fe";

/** Human labels for the two playlist natures (the "all" filter case is handled at each call site). */
export const NATURE_LABEL = {
  site: "On site",
  both: "On site + YouTube",
} as const;

/** A pending confirmation handled by the shared AlertDialog. */
export type Confirm = {
  title: string;
  body: string;
  confirmText: string;
  ok: () => void;
};

/* ---- platform marks: the real CMS YouTube logo + a clean globe for on-site ---- */
export const YtMark = () => (
  <img
    src={youtubeLogo}
    alt="YouTube"
    style={{ height: 13, width: "auto", verticalAlign: "middle" }}
  />
);

export const DenmarkMark = () => (
  <img
    src={denmarkLogo}
    alt="Denmark"
    style={{ height: 13, width: "auto", verticalAlign: "middle" }}
  />
);

/** Selection check as inline SVG (bootstrap-icons check-circle / check-circle-fill paths). */
export const CheckCircle: React.FC<{
  filled?: boolean;
  size?: number;
  color?: string;
  title?: string;
  className?: string;
}> = ({
  filled,
  size = 16,
  color = filled ? PIN_ACTIVE : PIN_IDLE,
  title,
  className,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill={color}
    className={className}
    aria-hidden={title ? undefined : "true"}
    style={{ verticalAlign: "middle" }}
  >
    {title && <title>{title}</title>}
    {filled ? (
      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05" />
    ) : (
      <>
        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
        <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05" />
      </>
    )}
  </svg>
);

/** Favorite star as inline SVG (bootstrap-icons star / star-fill paths), shared by the
 * inline picker and the ExpandSheet favorite buttons. */
export const FavStar: React.FC<{
  filled?: boolean;
  size?: number;
  color?: string;
}> = ({ filled, size = 16, color = filled ? "#f5b301" : PIN_IDLE }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill={color}
    aria-hidden="true"
    style={{ verticalAlign: "middle" }}
  >
    {filled ? (
      <path d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z" />
    ) : (
      <path d="M2.866 14.85c-.078.444.36.791.746.593l4.39-2.256 4.389 2.256c.386.198.824-.149.746-.592l-.83-4.73 3.522-3.356c.33-.314.16-.888-.282-.95l-4.898-.696L8.465.792a.513.513 0 0 0-.927 0L5.354 5.12l-4.898.696c-.441.062-.612.636-.283.95l3.523 3.356-.83 4.73zm4.905-2.767-3.686 1.894.694-3.957a.56.56 0 0 0-.163-.505L1.71 6.745l4.052-.576a.53.53 0 0 0 .393-.288L8 2.223l1.847 3.658a.53.53 0 0 0 .393.288l4.052.575-2.906 2.77a.56.56 0 0 0-.163.506l.694 3.957-3.686-1.894a.5.5 0 0 0-.461 0z" />
    )}
  </svg>
);

export const isOnSitePlaylist = (p: Playlist): boolean =>
  !(p.youtubeChannels && p.youtubeChannels.length > 0);

/** Selected-playlist chip, shared by the inline picker and the ExpandSheet Selected column.
 * The primary playlist gets a dull-yellow background. */
export const PlaylistChip: React.FC<{
  name: string;
  primary?: boolean;
  title?: string;
  /** Show a grip glyph before the name (chips are reorderable when more than one is selected). */
  showDragHandle?: boolean;
  onClick?: () => void;
  onRemove: () => void;
}> = ({ name, primary, title, showDragHandle, onClick, onRemove }) => {
  const [hovered, setHovered] = React.useState(false);
  return (
  <Tooltip content={primary ? "Primary playlist" : undefined}>
  <div
    className="social-chip gap-1 p-1"
    title={primary ? undefined : title}
    onMouseEnter={() => setHovered(true)}
    onMouseLeave={() => setHovered(false)}
    style={{
      minWidth: 0,
      width: "100%",
      position: "relative",
      cursor: onClick ? "pointer" : undefined,
      ...(primary
        ? { backgroundColor: "#e7f1ff", borderColor: "#b6d4fe" }
        : {}),
    }}
  >
    {showDragHandle && (
      <i
        className="bi bi-grip-vertical flex-shrink-0"
        aria-hidden="true"
        title="Drag to reorder"
        style={{ cursor: "grab", color: "#adb5bd", fontSize: 14 }}
      />
    )}
    <div
      className="d-flex align-items-center gap-1 flex-grow-1"
      style={{ minWidth: 0 }}
      onClick={onClick}
    >
      <span
        className={`text-truncate flex-grow-1 ${showDragHandle ? "" : "ps-2"}`}
        style={{ fontSize: 13 }}
      >
        {name}
      </span>
    </div>
    <i
      className="bi bi-x fs-4 flex-shrink-0"
      role="button"
      tabIndex={0}
      title="Remove from playlist"
      aria-label={`Remove ${name}`}
      style={{
        cursor: "pointer",
        color: hovered ? "#212529" : "#adb5bd",
        transition: "color .15s ease",
      }}
      onClick={(e) => {
        e.stopPropagation();
        onRemove();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          onRemove();
        }
      }}
    />
  </div>
  </Tooltip>
  );
};

export const ChannelMappings: React.FC<{ p: Playlist }> = ({ p }) => {
  const yt = p.youtubeChannels || [];
  const dk = p.denmarkChannels || [];
  if (yt.length === 0 && dk.length === 0) return null;
  return (
    <div className="d-flex flex-row flex-wrap align-items-center gap-2 mt-1">
      {yt.map((c, i) => (
        <div
          key={`yt-${i}`}
          className="text-muted small text-truncate d-flex align-items-center gap-1"
        >
          <YtMark />
          <span className="text-truncate">{c.playlist_name}</span>
        </div>
      ))}
      {dk.map((c, i) => (
        <div
          key={`dk-${i}`}
          className="text-muted small text-truncate d-flex align-items-center gap-1"
        >
          <DenmarkMark />
          <span className="text-truncate">{c.channelName}</span>
        </div>
      ))}
    </div>
  );
};

// "Playlist" glyph — three stacked lines + a play mark — built as SVG (no image asset).
// On-site keeps a plain play triangle; on-site+YouTube swaps that triangle for the YouTube mark.
export const PlaylistIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 15,
  color = "#5b6470",
}) => (
  <svg
    width={size}
    height={size * 0.73}
    viewBox="0 0 22 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="0" y="0" width="13" height="2.2" rx="1.1" fill={color} />
    <rect x="0" y="6.4" width="13" height="2.2" rx="1.1" fill={color} />
    <rect x="0" y="12.8" width="13" height="2.2" rx="1.1" fill={color} />
    <path d="M16 2 L22 8 L16 14 Z" fill={color} />
  </svg>
);

export const PlaylistYoutubeIcon: React.FC<{
  size?: number;
  color?: string;
}> = ({ size = 15, color = "#5b6470" }) => (
  <svg
    width={size}
    height={size * 0.73}
    viewBox="0 0 22 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="0" y="0" width="13" height="2.2" rx="1.1" fill={color} />
    <rect x="0" y="6.4" width="13" height="2.2" rx="1.1" fill={color} />
    <rect x="0" y="12.8" width="13" height="2.2" rx="1.1" fill={color} />
    <path d="M16 2 L22 8 L16 14 Z" fill="#FF0000" />
  </svg>
);

// Thin outline chevrons + vertical three-dot menu glyph — built as SVG (no font icon).
export const ChevronUpIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 15,
  color = "currentColor",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 10 L8 6 L12 10"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ChevronDownIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 15,
  color = "currentColor",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 6 L8 10 L12 6"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ThreeDotsVerticalIcon: React.FC<{
  size?: number;
  color?: string;
}> = ({ size = 15, color = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="8" cy="3" r="1.4" fill={color} />
    <circle cx="8" cy="8" r="1.4" fill={color} />
    <circle cx="8" cy="13" r="1.4" fill={color} />
  </svg>
);

// Compact inline mark used on the selected-playlist chips and the Manage sheet header.
export const NatureMark: React.FC<{ p: Playlist }> = ({ p }) =>
  p.nature === "site" ? (
    <i
      className="bi bi-globe2"
      style={{ fontSize: 12, color: "#5b6470" }}
      title={NATURE_LABEL.site}
    />
  ) : (
    <span className="d-inline-flex" title={NATURE_LABEL.both}>
      <YtMark />
    </span>
  );

// Distinct, offline-safe thumbnail used when a video has no thumb URL or the image fails to load.
export const fallbackThumb = (seed: string) => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const hue = Math.abs(h * 137) % 360; // golden-angle spread so similar ids look different
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='160' height='90'><rect width='160' height='90' fill='hsl(${hue},42%,84%)'/><path d='M68 33 l26 12 -26 12 z' fill='hsl(${hue},38%,52%)'/></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};
